import { useState, useMemo } from "react"
import { Text, Collapse } from "@mantine/core"
import BlackSelectInput from "app/views/components/inputs/BlackSelectInput"
import BlackNumberInput from "app/views/components/inputs/BlackNumberInput"
import MoreLoanOptionsPanel from "./more-loan-options-panel"
import MergeLoansPaydownInfoMessage from "./merge-loans-paydown-info-message"
import { LoanParams } from "app/models/loan-params"
import { useSelector } from 'react-redux'
import { getIndexRateOptions } from "app/state/ducks/index-rate-options/selectors"
import { getFacilities } from "app/state/ducks/facilities/selectors"
import { ReactComponent as ChevronRightIcon } from 'app/styles/chevronRight.svg'
import { ReactComponent as TrashIcon } from 'app/styles/trash.svg'
import { ReactComponent as ChevronDownIcon } from 'app/styles/chevronDown.svg'

type NewLoan = {
    indexRateOption: string
    amount: string
    borrower: string
    startDate: Date | null
    maturityDate: Date | null
    interestBaseRate: string
    interestBaseRateRounding: string
    dayBasis: string
    margin: string
    cas: string
    allInRate: string
    facilityId: string
    currency: string
    // Rate to the facility base currency; '' when they match
    fxRate: string
    isNonProRata: boolean
    rounding: string
    pikPremium: string
    pikInterestPercent: string
    cashInterestPercent: string
    newLoanWithPik: string
}

type MergeAddNewLoanProps = {
    index: number
    loan: NewLoan
    mergeType: string
    selectedLoans?: LoanParams[]
    onIndexRateOptionChange: (index: number, value: string) => void
    onAmountChange: (index: number, value: string) => void
    onFieldChange: (index: number, field: keyof NewLoan, value: string | Date | null) => void
    onRemove: (index: number) => void
    showRemoveButton: boolean
}

export default function MergeAddNewLoan({
    index,
    loan,
    mergeType,
    selectedLoans,
    onIndexRateOptionChange,
    onAmountChange,
    onFieldChange,
    onRemove,
    showRemoveButton
}: MergeAddNewLoanProps) {
    const allFacilities = useSelector(getFacilities)
    const indexRateOptions = useSelector(getIndexRateOptions)

    // Find the specific facility for this loan
    const facility = useMemo(() => {
        if (!allFacilities || !loan.facilityId) return null
        return allFacilities.find(f => f.id === loan.facilityId)
    }, [allFacilities, loan.facilityId])

    // Extract available term index options for this facility
    const facilityTermIndexOptions = useMemo(() => {
        if (!facility?.iroValues || !indexRateOptions) {
            return []
        }

        // Extract indexOption IDs from facility's iroValues
        const facilityIndexOptionIds = facility.iroValues
            .map((iro: any) => iro.indexOption?.id)
            .filter((id): id is string => Boolean(id))

        // Find matching indexRateOptions and filter for TermIndex type
        const availableOptions = indexRateOptions
            .filter(option =>
                option.id &&
                facilityIndexOptionIds.includes(option.id) &&
                option.indexType === 'TermIndex'
            )
            .map(option => ({
                value: option.id || '',
                label: option.indexOption
            }))

        return availableOptions
    }, [facility, indexRateOptions])

    const [isExpanded, setIsExpanded] = useState(false)

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded)
    }
    return (
        <div className='merge-add-new-loan-container'>
            <div className="new-loan-header">
                <Text className="new-loan-header-text">New Loan</Text>
                <div className="new-loan-header-actions">
                    {showRemoveButton && <TrashIcon onClick={() => onRemove(index)} />}
                </div>
            </div>
            <div className="merge-loan-input-field-tuple">
                <BlackSelectInput
                    label="Index Rate Option"
                    data={facilityTermIndexOptions}
                    value={loan.indexRateOption}
                    onChange={(value) => onIndexRateOptionChange(index, value || '')}
                    classNames={{
                        input: "merge-form-editable-select"
                    }}
                    styles={{
                        label: {
                            padding: '10px 0px'
                        },
                        rightSection: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%'
                        }
                    }}
                    rightSection={<ChevronDownIcon />}
                />
                <BlackNumberInput
                    label="Amount"
                    value={loan.amount ? parseFloat(loan.amount) : ''}
                    onChange={(value) => onAmountChange(index, value?.toString() || '')}
                    precision={2}
                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                    styles={{
                        label: {
                            padding: '10px 0px'
                        }
                    }}
                />
            </div>
            {mergeType === 'single' && (
                <MergeLoansPaydownInfoMessage
                    selectedLoans={selectedLoans}
                    newLoanAmount={loan.amount}
                />
            )}
            <div className="more-loan-options" onClick={toggleExpanded}>
                <div style={{ width: '13px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {isExpanded ? (
                        <ChevronDownIcon style={{ width: '14px', height: '26px' }} />
                    ) : (
                        <ChevronRightIcon style={{ width: '7px', height: '13px' }} />
                    )}
                </div>
                <Text className="more-loan-options-text">More Loan Options</Text>
            </div>

            {isExpanded && (
                <div className="more-loan-options-divider-line" />
            )}

            <Collapse in={isExpanded} w="100%">
                <MoreLoanOptionsPanel
                    index={index}
                    loan={loan}
                    selectedLoans={selectedLoans}
                    onFieldChange={onFieldChange}
                />
            </Collapse>
        </div>
    )
}
